import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, createSlots as _createSlots, TransitionGroup as _TransitionGroup } from "vue"

const _hoisted_1 = { class: "view-tabs-row" }
const _hoisted_2 = ["aria-label", "aria-owns"]
const _hoisted_3 = ["onMousedown"]
const _hoisted_4 = ["size", "aria-label", "data-testid", "onKeydown"]
const _hoisted_5 = ["id", "aria-selected", "tabindex", "data-testid", "onClick", "onKeydown"]
const _hoisted_6 = ["aria-label", "data-testid"]
const _hoisted_7 = {
  key: 1,
  class: "unsaved-dot",
  "data-testid": "table-views-unsaved"
}
const _hoisted_8 = { class: "menu-notice" }
const _hoisted_9 = { class: "menu-shortcut" }
const _hoisted_10 = { class: "menu-shortcut" }
const _hoisted_11 = { class: "menu-shortcut" }

import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch
} from 'vue';
import type { ComponentPublicInstance } from 'vue';
import { useStore } from 'vuex';

import TableViewExportModal from '@shell/components/TableViews/TableViewExportModal.vue';
import TableViewShareModal from '@shell/components/TableViews/TableViewShareModal.vue';
import TableViewPublishModal from '@shell/components/TableViews/TableViewPublishModal.vue';
import { useDragReorder } from '@shell/composables/useDragReorder';
import { useI18n } from '@shell/composables/useI18n';
import { useSavedTableViews } from '@shell/composables/useSavedTableViews';
import { useGlobalTableViews } from '@shell/composables/useGlobalTableViews';
import { isMac, shortcutLabel } from '@shell/utils/platform';
import { shareTableView, sharedTableKey } from '@shell/utils/table-views/share';
import type { SharedTableView } from '@shell/utils/table-views/share';
import { registerTableViewShortcuts } from '@shell/utils/table-views/shortcuts';
import type { TableViewShortcutAction } from '@shell/utils/table-views/shortcuts';
import { randomStr } from '@shell/utils/string';
import { isViewDirty, isViewModified, persistenceIdOf, selectedViewIdFor } from '@shell/utils/table-views/views';
import { TABLE_VIEWS } from '@shell/store/prefs';
import { ALL_TAB_KEY, defaultViewIdFor, tabOrderFor } from '@shell/utils/table-views/global';
import type { TableViewSaved, TableViewState } from '@shell/types/table-views';
import { RcDropdown, RcDropdownItem, RcDropdownSeparator, RcDropdownTrigger } from '@components/RcDropdown';


/** How close to an end of the strip a held tab has to be before the strip scrolls that way */
const TAB_SCROLL_EDGE = 56;

const TAB_SCROLL_STEP = 12;

const TAB_SCROLL_SETTLE_MAX_MS = 1200;

const TAB_FLASH_MS = 600;

/** Longer than a growl's usual 5s: it is the only way back */
const UNDO_TIMEOUT = 10000;

interface Tab {
  id: string | null;
  name: string;
  view?: TableViewSaved;
  isDefaultTab?: boolean;
  /** Shared with everyone rather than the user's own */
  shared?: boolean;
}

/** The table's own tab has no id, and null also means "nothing" */
const LOCKED_TAB_COUNT = 1;


export default /*@__PURE__*/_defineComponent({
  __name: 'TableViewTabs',
  props: {
    view: {},
    viewCounts: { default: () => ({}) },
    matchCount: { default: 0 },
    resourceType: { default: '' },
    tableViewsPage: { default: null },
    initialViewId: {}
  },
  emits: ["update:view", "export", "tab-queries"],
  setup(__props: any, { emit: __emit }) {

/**
 * The saved view tabs above a resource table: one per saved view, plus the table's own tab. The
 * applied view lives in the `view` prop; the filter and View menu below are TableViewControls
 */
function tabKey(tab: { id?: string | null }) {
  return tab.id || ALL_TAB_KEY;
}

const props = __props;

const emit = __emit;

const store = useStore();

const { t } = useI18n(store);

const {
  globalViews, sharedViews, loaded: sharedLoaded, canShare, isShared, canEdit: canEditShared, canRemove: canRemoveShared,
  share, update: updateShared, remove: removeShared
} = useGlobalTableViews(() => props.resourceType, () => props.tableViewsPage);

const {
  savedViews, defaultViewId: ownDefaultViewId, allTabIndex, tabOrder, persistAll, persist, unusedViewName
} = useSavedTableViews(() => props.resourceType, () => props.tableViewsPage, () => sharedViews.value);

/** The user's views, less any that is now shared with everyone */
const ownViews = computed(() => savedViews.value.filter((view) => !isShared(view.id)));

/** Every view the strip shows: shared, then the user's */
const allViews = computed(() => sharedViews.value.concat(ownViews.value));

/** The view the list opens on: the user's choice, else the page's shared default */
const defaultViewId = computed(() => defaultViewIdFor(ownDefaultViewId.value, globalViews.value, allViews.value.map((view) => view.id)));

/** The table a shared view is for - see sharedTableKey */
const tableKey = computed(() => sharedTableKey(props.resourceType, props.tableViewsPage));

const root = ref<HTMLElement | null>(null);

const tabStrip = ref<HTMLElement | null>(null);

/** Function refs, as there is a set per tab */
const tabWraps = new Map<string, HTMLElement>();

const tabButtons = new Map<string, HTMLElement>();

const tabCarets = new Map<string, Element | ComponentPublicInstance>();

const renameInputs = new Map<string, HTMLInputElement>();

const keepRef = <T, >(map: Map<string, T>, key: string, el: T | null) => {
  if (el) {
    map.set(key, el);
  } else {
    map.delete(key);
  }
};

/**
 * `undefined` if nothing is picked yet, `null` for the table's own tab, else a saved view's id. The
 * first two must differ, or a saved view holding the same config would be matched instead
 */
const pickedViewId = ref<string | null | undefined>(props.initialViewId);

/** Unsaved edits per tab, kept while the page is open so leaving a tab doesn't lose them */
const drafts = ref<Record<string, TableViewState>>({});

/**
 * An export: the tab's name, its row count (null if unknown) and, for a tab not on screen, its view.
 * A share: the tab's view as text. An import: the saved view it replaces the settings of, or null
 * from the table's own tab, which can't be changed: a new view is added to take it
 */
const modal = ref<
  { kind: 'export', name: string, count: number | null, view?: TableViewState } |
  { kind: 'share', text: string, hasFilter: boolean } |
  { kind: 'import', tabId: string | null } |
  { kind: 'publish', view: TableViewSaved } |
  null
>(null);

const renamingId = ref<string | null>(null);

const renameDraft = ref('');

const tabBounds = ref<number[] | null>(null);

const openTabMenuId = ref<string | null>(null);

const flashTabId = ref<string | null>(null);

let flashTimer: ReturnType<typeof setTimeout> | undefined;

let flashFrame = 0;

let tabScrollFrame = 0;

const shortcuts = computed(() => {
  const modifier = isMac ? '⌘' : 'Ctrl';

  return {
    save:      shortcutLabel([modifier, 'S']),
    saveAsNew: shortcutLabel([modifier, 'Shift', 'S']),
    duplicate: shortcutLabel([modifier, 'D']),
  };
});

/** The saved strip: the view the list opens on leads, then the table's own tab and the other views */
const baseTabs = computed<Tab[]>(() => {
  const byKey: Record<string, Tab> = {
    [ALL_TAB_KEY]: {
      id: null, name: t('tableViews.tabs.all'), isDefaultTab: true
    }
  };

  allViews.value.forEach((view) => {
    byKey[view.id] = {
      id: view.id, name: view.name, view, shared: isShared(view.id)
    };
  });

  const order = tabOrderFor({
    personal: savedViews.value, personalAllIndex: allTabIndex.value, global: globalViews.value, order: tabOrder.value
  });
  const tabs: Tab[] = order.map((key) => byKey[key]).filter(Boolean);

  // With no default set, the table's own tab leads
  const lead = tabs.findIndex((tab) => (defaultViewId.value ? tab.view?.id === defaultViewId.value : tab.isDefaultTab));

  if (lead <= 0) {
    return tabs;
  }

  return [tabs[lead]].concat(tabs.filter((_, i) => i !== lead));
});

const tabs = computed<Tab[]>(() => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  if (!tabDragOrder.value) {
    return baseTabs.value;
  }

  const byKey: Record<string, Tab> = {};

  baseTabs.value.forEach((tab) => {
    byKey[tabKey(tab)] = tab;
  });

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return tabDragOrder.value.map((key) => byKey[key]).filter(Boolean);
});

/** The tab the list opens on can't be dragged out of the front, nor anything dropped before it */
const selectedViewId = computed(() => selectedViewIdFor(allViews.value, props.view, pickedViewId.value));

/** The one tab Tab can land on (roving tabindex); the first if the current view isn't among them */
const focusableTabId = computed(() => {
  const list = tabs.value || [];
  const selected = list.find((tab) => tab.id === selectedViewId.value);

  return (selected || list[0])?.id;
});

const isDirty = computed(() => isViewDirty(allViews.value, props.view, pickedViewId.value));


const draftKey = (id: string | null | undefined) => id || '__default';

/**
 * What a tab is filtering by: the box for the tab on screen, else its held edits or saved query.
 * Counts are looked up by query, so they survive the list fetching a page
 */
const tabQuery = (tab: Tab) => {
  if (tab.id === selectedViewId.value) {
    return props.view.query || '';
  }

  const draft = drafts.value[draftKey(tab.id)];

  return (draft ? draft.query : tab.view?.query) || '';
};

const tabQueries = computed(() => Array.from(new Set(tabs.value.map((tab) => tabQuery(tab)))));

const tabCount = (tab: Tab) => props.viewCounts[tabQuery(tab)];

const tabLabel = (tab: Tab) => {
  const count = tabCount(tab);

  // undefined: not counted yet. null: the api wouldn't say
  return count === undefined || count === null ? tab.name : t('tableViews.tabs.count', { name: tab.name, count });
};

const isTabDirty = (tab: Tab) => {
  if (tab.id === selectedViewId.value) {
    return isDirty.value;
  }

  return !!drafts.value[draftKey(tab.id)];
};

/** Compared by table masthead, so a second table on the page keeps its own shortcuts */
const ownsTarget = (target: EventTarget | null) => {
  // The root is a fragment (the modal is beside the tabs), so it has a ref of its own
  const el = root.value;

  if (!el?.closest || !(target instanceof Element)) {
    return false;
  }

  // The rows count as the list too; the masthead is the fallback outside table views layout
  const listOf = (node: Element) => node.closest('.has-table-views') || node.closest('.fixed-header-actions');
  const mine = listOf(el);

  return !!mine && listOf(target) === mine;
};

const tabWrap = (tab: Tab) => tabWraps.get(tabKey(tab));

const tabButton = (tab: Tab) => tabButtons.get(tabKey(tab));

const uid = useId();

const tabElementId = (tab: Tab) => `${ uid }-${ tabKey(tab) }`;

/** A component, so its element is one step down */
const tabCaret = (tab: Tab) => {
  const cmp = tabCarets.get(tabKey(tab));

  return cmp instanceof Element ? cmp : cmp?.$el;
};

/** The menu is positioned against its tab, so it closes when the tab moves */
const closeTabMenus = () => {
  openTabMenuId.value = null;
};

/**
 * Measured once as the drag begins, relative to the list: measuring the live order swaps tabs back
 * and forth
 */
const captureTabSlots = () => {
  const list = tabStrip.value?.querySelector('.view-tabs-list');

  if (!list) {
    return;
  }

  const base = list.getBoundingClientRect().left;
  const boxes = baseTabs.value.map((tab) => {
    const rect = tabWrap(tab)?.getBoundingClientRect();

    return rect ? { left: rect.left - base, right: rect.right - base } : null;
  }).filter(Boolean) as { left: number, right: number }[];

  // The middle of the gap between two tabs
  tabBounds.value = boxes.slice(0, -1).map((box, i) => (box.right + boxes[i + 1].left) / 2);
};

const tabIndexAt = (clientX: number) => {
  const list = tabStrip.value?.querySelector('.view-tabs-list');

  if (!list) {
    return 0;
  }

  const x = clientX - list.getBoundingClientRect().left;
  const bounds = tabBounds.value || [];
  let i = 0;

  while (i < bounds.length && x >= bounds[i]) {
    i++;
  }

  return i;
};

/** A frame loop, so holding still at the edge keeps it scrolling */
const runTabScroll = () => {
  const step = () => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (!tabDragMoved.value) {
      return;
    }

    const strip = tabStrip.value;

    if (strip) {
      const rect = strip.getBoundingClientRect();

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      if (tabDragPointer.value < rect.left + TAB_SCROLL_EDGE) {
        strip.scrollLeft -= TAB_SCROLL_STEP;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        placeDraggedTab();
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      } else if (tabDragPointer.value > rect.right - TAB_SCROLL_EDGE) {
        strip.scrollLeft += TAB_SCROLL_STEP;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        placeDraggedTab();
      }
    }

    tabScrollFrame = requestAnimationFrame(step);
  };

  cancelAnimationFrame(tabScrollFrame);
  tabScrollFrame = requestAnimationFrame(step);
};

/**
 * The user's order, as their views' order and the table's own tab's place among them; and, once
 * there are shared views, as every tab's key
 */
const persistOrder = (order: string[], own: TableViewSaved[] = ownViews.value) => {
  const byId: Record<string, TableViewSaved> = {};

  own.forEach((view) => {
    byId[view.id] = view;
  });

  const ordered = order.map((key) => byId[key]).filter(Boolean);
  const allIndex = order.slice(0, order.indexOf(ALL_TAB_KEY)).filter((key) => byId[key]).length;
  // Any the order doesn't name yet go last
  const rest = own.filter((view) => !order.includes(view.id));

  persistAll(ordered.concat(rest), ownDefaultViewId.value, allIndex, sharedViews.value.length || tabOrder.value ? order : undefined);
};

/** The table's own tab isn't a saved view, so its place is kept beside them */
const {
  heldId: heldTabKey, order: tabDragOrder, moved: tabDragMoved, pointer: tabDragPointer, start: armTabDrag, place: placeDraggedTab
} = useDragReorder({
  axis:         'x',
  initialOrder: () => baseTabs.value.map(tabKey),
  measure:      captureTabSlots,
  indexAt:      tabIndexAt,
  firstMovable: () => LOCKED_TAB_COUNT,
  onBegin:      () => {
    closeTabMenus();
    // Otherwise the pointer selects the tab names it crosses
    window.getSelection()?.removeAllRanges();
    runTabScroll();
  },
  onEnd: () => {
    cancelAnimationFrame(tabScrollFrame);
    tabBounds.value = null;
  },
  onCommit: persistOrder,
});

const startTabDrag = (tab: Tab, event: MouseEvent) => {
  const key = tabKey(tab);

  if (event.button !== 0 || renamingId.value || baseTabs.value.findIndex((t) => tabKey(t) === key) < LOCKED_TAB_COUNT) {
    return;
  }

  armTabDrag(key, event);
};

const forgetDraft = (id: string | null | undefined) => {
  const key = draftKey(id);

  if (!drafts.value[key]) {
    return;
  }

  const rest = { ...drafts.value };

  delete rest[key];
  drafts.value = rest;
};

const rememberDraft = (id: string | null | undefined) => {
  const key = draftKey(id);

  if (isDirty.value) {
    drafts.value = { ...drafts.value, [key]: { ...props.view } };

    return;
  }

  forgetDraft(id);
};

/** The state a saved view applies, or the table's own tab's - always the table as it comes - for none */
const viewStateOf = (saved?: Partial<TableViewState> | null): TableViewState => ({
  query:          saved?.query || '',
  columns:        saved?.columns || null,
  columnOrder:    saved?.columnOrder || null,
  labelColumns:   saved?.labelColumns || [],
  groupBy:        saved?.groupBy || null,
  sort:           saved?.sort || null,
  sortDescending: saved?.sortDescending || false,
});

/**
 * @param saved the view to show, or null for the table's own tab
 * @param useDraft bring back unsaved edits left on the tab; off when putting it back as saved
 */
const applyView = (saved: TableViewSaved | null, useDraft = true) => {
  const from = selectedViewId.value;
  const to = saved?.id || null;
  const moving = from !== to;

  // Clicking the tab already on screen doesn't throw its edits away
  if (!moving && useDraft) {
    pickedViewId.value = to;

    return;
  }

  // Before the pick moves: unsaved changes are measured against the tab being left
  if (moving) {
    rememberDraft(from);
  }

  pickedViewId.value = to;

  const draft = useDraft ? drafts.value[draftKey(to)] : null;

  if (draft) {
    emit('update:view', { ...draft });

    return;
  }

  emit('update:view', viewStateOf(saved));
};

/** The tab in front of the user */
const selectedTab = () => (tabs.value || []).find((tab) => tab.id === selectedViewId.value);

/** A tab's state as it stands: what is on screen for the tab in front, its held edits for the rest */
const tabState = (tab: Tab): TableViewState => {
  if (tab.id === selectedViewId.value) {
    return { ...props.view };
  }

  return drafts.value[draftKey(tab.id)] || viewStateOf(tab.view);
};

/** Put a tab in front as it was left, without keeping anything for the one it replaces */
const showTab = (tab?: Tab) => {
  const id = tab?.id || null;
  const draft = drafts.value[draftKey(id)];

  pickedViewId.value = id;
  emit('update:view', draft ? { ...draft } : viewStateOf(tab?.view));
};

// The menu's actions are for the tab it was opened on, which need not be the one in front
const discardChanges = (tab = selectedTab()) => {
  if (!tab) {
    return;
  }

  forgetDraft(tab.id);

  if (tab.id === selectedViewId.value) {
    applyView(tab.view || null, false);
  }
};

/**
 * Focus a view's tab and bring it into sight. `toEnd` runs the strip to its end, where a new tab is
 * before the strip has laid it out
 */
const focusTab = (id: string | null, toEnd = false) => {
  nextTick(() => {
    const tab = (tabs.value || []).find((candidate) => candidate.id === id);
    const btn = tab && tabButton(tab);

    if (!btn) {
      return;
    }

    // Focus would scroll the strip itself, fighting the run to the end
    btn.focus({ preventScroll: !!toEnd });

    if (!toEnd) {
      btn.scrollIntoView({ block: 'nearest', inline: 'nearest' });

      return;
    }

    requestAnimationFrame(() => {
      const strip = tabStrip.value;

      if (strip) {
        strip.scrollLeft = strip.scrollWidth;
      }
    });
  });
};

const openRename = (saved?: { id?: string, name?: string } | null) => {
  if (!saved?.id) {
    return;
  }

  renamingId.value = saved.id;
  renameDraft.value = saved.name || '';

  nextTick(() => {
    const input = renameInputs.get(saved.id as string);

    input?.focus();
    input?.select();
  });
};

const nextNewViewName = () => unusedViewName(t('tableViews.tab.newViewName'), 1);

const duplicateView = (saved: Omit<TableViewSaved, 'id'>) => {
  const name = unusedViewName(`${ saved.name } ${ t('tableViews.tab.copySuffix') }`, 2);
  const copy = {
    ...saved, id: randomStr(8), name
  };

  persist(savedViews.value.concat([copy]));
  applyView(copy);
  focusTab(copy.id, true);
  nextTick(() => openRename(copy));
};

/**
 * Saves the tab with its unsaved changes as a new view, landing like a copy: in front, its name
 * open for typing. The changes go with it, so the tab they were copied from is left as saved
 */
const openSaveAsNew = (tab = selectedTab()) => {
  if (!tab || (!isTabDirty(tab) && pickedViewId.value === undefined)) {
    return;
  }

  duplicateView({ ...viewStateOf(tabState(tab)), name: tab.name });
  forgetDraft(tab.id);
};

const saveChanges = (tab = selectedTab()) => {
  // The table's own tab can't be saved over; keeping its changes is Save as New, not this
  if (!tab?.view || !isTabDirty(tab)) {
    return;
  }

  const state = viewStateOf(tabState(tab));

  if (tab.shared) {
    // Not the user's to change: Save as New keeps the changes as a view of their own
    if (canEditShared(tab.id)) {
      updateShared(tab.view.id, state).then((saved) => saved && forgetDraft(tab.id));
    }

    return;
  }

  persist(savedViews.value.map((v) => (v.id === tab.view?.id ? { ...v, ...state } : v)));
  forgetDraft(tab.id);
};

/** Saved straight away with the table's defaults, and renamed once it is worth naming */
const addView = (): TableViewSaved => {
  const view: TableViewSaved = {
    ...viewStateOf(), id: randomStr(8), name: nextNewViewName()
  };

  persist(savedViews.value.concat([view]));
  applyView(view);
  focusTab(view.id, true);

  return view;
};

/** Focus follows: with a roving tabindex, the tab left behind is no longer reachable */
const goToTab = (tab: Tab) => {
  applyView(tab.view || null);
  focusTab(tab.id);
};

/** Moving focus picks the view too, as the product's other tabs do */
const stepTab = (delta: number) => {
  const list = tabs.value || [];

  if (list.length < 2) {
    return;
  }

  // Not always the tab in force: deleting a view leaves focus on the one before
  const focused = list.findIndex((tab) => tabButton(tab) === document.activeElement);
  const at = focused >= 0 ? focused : list.findIndex((tab) => tab.id === focusableTabId.value);
  const next = list[((at < 0 ? 0 : at) + delta + list.length) % list.length];

  goToTab(next);
};

const edgeTab = (which: string) => {
  const list = tabs.value || [];
  const next = which === 'first' ? list[0] : list[list.length - 1];

  if (next) {
    goToTab(next);
  }
};

/**
 * The chevron is out of the tab sequence, so the down arrow opens the menu. Flagged as a key press
 * first, so the menu focuses its first row
 */
const openTabMenu = (tab: Tab) => {
  const caret = tabCaret(tab);

  if (!caret) {
    return;
  }

  caret.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
  caret.click();
};

/**
 * The chevron takes focus back on close, but it answers no arrows - return it to the tab, unless a
 * click has moved focus elsewhere
 */
const onTabMenuToggle = (tab: Tab, open: boolean) => {
  openTabMenuId.value = open ? tabKey(tab) : null;

  if (open) {
    return;
  }

  nextTick(() => {
    if (document.activeElement === tabCaret(tab)) {
      tabButton(tab)?.focus();
    }
  });
};

/** Waits for the scroll to finish rather than a fixed delay; the cap covers one that never does */
const flashTabWhenScrolled = (strip: HTMLElement, key: string) => {
  clearTimeout(flashTimer);
  cancelAnimationFrame(flashFrame);

  const deadline = Date.now() + TAB_SCROLL_SETTLE_MAX_MS;

  const settle = () => {
    if (strip.scrollLeft > 1 && Date.now() < deadline) {
      flashFrame = requestAnimationFrame(settle);

      return;
    }

    flashTabId.value = key;
    flashTimer = setTimeout(() => {
      flashTabId.value = null;
    }, TAB_FLASH_MS);
  };

  settle();
};

const commitRename = () => {
  const id = renamingId.value;
  const name = (renameDraft.value || '').trim();
  const saved = allViews.value.find((v) => v.id === id);

  renamingId.value = null;
  renameDraft.value = '';

  if (!name || !saved || name === saved.name) {
    return;
  }

  if (isShared(saved.id)) {
    if (canEditShared(saved.id)) {
      updateShared(saved.id, { name });
    }

    return;
  }

  persist(savedViews.value.map((v) => (v.id === id ? { ...v, name } : v)));
};

const cancelRename = () => {
  renamingId.value = null;
  renameDraft.value = '';
};

const openExport = (tab: Tab) => {
  // The tab on screen is exported as it stands; another as it would open, with any held edits
  if (tab.id === selectedViewId.value) {
    modal.value = {
      kind: 'export', name: tab.name, count: props.matchCount
    };

    return;
  }

  const draft = drafts.value[draftKey(tab.id)];
  const saved = tab.view;
  const count = tabCount(tab);

  modal.value = {
    kind:  'export',
    name:  tab.name,
    count: typeof count === 'number' ? count : null,
    view:  draft ? { ...draft } : viewStateOf(saved),
  };
};

const openShare = (tab: Tab) => {
  // The tab on screen as it stands, another with any edits held for it
  const view: SharedTableView = { ...viewStateOf(tabState(tab)), name: tab.name };

  modal.value = {
    kind: 'share', text: shareTableView(tableKey.value, view, persistenceIdOf(store.getters['prefs/get'](TABLE_VIEWS))), hasFilter: !!view.query
  };
};

const openImport = (tab: Tab) => {
  modal.value = { kind: 'import', tabId: tab.id };
};

const closeModal = () => {
  modal.value = null;
};

const publishFromModal = () => {
  if (modal.value?.kind === 'publish') {
    publish(modal.value.view);
  }
};

/** The dialogs are only there while open */
const modalFrame = { show: true, onClose: closeModal };

/** The table's own tab included: copying it is how to start a view from the table as it comes */
const duplicateTab = (tab: Tab) => {
  duplicateView(tab.view || { ...viewStateOf(), name: t('tableViews.tabs.all') });
};

/**
 * The table as it comes - no filter, its own grouping, columns and sort - on the tab the menu was
 * opened on, as changes to save or discard
 */
const resetTab = (tab: Tab) => {
  const reset = viewStateOf();

  if (tab.id === selectedViewId.value) {
    // Pinned, or a view matched by its config would give way to the table's own tab
    pickedViewId.value = tab.id;
    emit('update:view', reset);

    return;
  }

  if (isViewModified(viewStateOf(tab.view))) {
    drafts.value = { ...drafts.value, [draftKey(tab.id)]: reset };
  } else {
    forgetDraft(tab.id);
  }
};

const duplicateCurrent = () => {
  const tab = tabs.value.find((candidate) => candidate.id === selectedViewId.value);

  if (tab) {
    duplicateTab(tab);
  }
};

/** Picking the table's own tab is how to go back to no default */
const setDefaultView = (tab: Tab) => {
  // Over a page's shared default, the table's own tab is a choice to keep
  const ownTab = globalViews.value.defaultViewId ? ALL_TAB_KEY : null;

  persistAll(savedViews.value, tab.isDefaultTab ? ownTab : tab.view?.id || null);

  // The tab just moved to the front, so scroll there and flash it to say why the strip moved
  const key = tabKey(tab.isDefaultTab ? { id: null } : { id: tab.view?.id });

  nextTick(() => {
    const strip = tabStrip.value;

    if (!strip) {
      return;
    }

    strip.scrollTo({ left: 0, behavior: 'smooth' });
    flashTabWhenScrolled(strip, key);
  });
};

const isDefaultTab = (tab: Tab) => (tab.isDefaultTab ? !defaultViewId.value : defaultViewId.value === tab.view?.id);

/**
 * Deleted at once and offered back, rather than confirmed first. Undo restores its place, default
 * and held edits too
 */
const deleteView = async(saved?: TableViewSaved) => {
  if (!saved) {
    return;
  }

  const shared = isShared(saved.id);

  if (shared && !canRemoveShared(saved.id)) {
    return;
  }

  const wasSelected = selectedViewId.value === saved.id;
  const wasDefault = ownDefaultViewId.value === saved.id;
  const at = savedViews.value.findIndex((v) => v.id === saved.id);
  // The table's own tab is placed among the views by how many come before it, one fewer once this goes
  const beforeAll = at >= 0 && at < allTabIndex.value;
  // Its place in the user's order, which deleting it takes out
  const orderBefore = tabOrder.value;
  const draft = drafts.value[draftKey(saved.id)];
  // Taken before the view goes: the tab to its left, or to its right when it was the first
  const list = tabs.value || [];
  const index = list.findIndex((tab) => tab.id === saved.id);
  const neighbour = list[index > 0 ? index - 1 : index + 1];

  // Gone for everyone, so only once the cluster agrees
  const removed = shared ? await removeShared(saved.id) : null;

  if (removed && !removed.ok) {
    return;
  }

  const undoShared = removed?.undo;

  // Its edits go with it, so nothing of it is left on screen for the tab taking its place
  forgetDraft(saved.id);

  if (!shared) {
    persistAll(savedViews.value.filter((v) => v.id !== saved.id), ownDefaultViewId.value, allTabIndex.value - (beforeAll ? 1 : 0));
  }

  if (wasSelected) {
    showTab(neighbour);
  }

  if (neighbour) {
    focusTab(neighbour.id);
  }

  const growl = {
    title:   t('tableViews.tab.deleted'),
    // Raw, since the growl renders text: escaped, the quotes would show as `&quot;`
    message: t(shared ? 'tableViews.tab.deletedSharedMessage' : 'tableViews.tab.deletedMessage', { name: saved.name }, true),
  };

  // A shared view comes back only for a user allowed to put it back
  if (shared && !undoShared) {
    store.dispatch('growl/success', growl);

    return;
  }

  // The undo only lives on the growl: the notification centre stores a copy, and a callback can't
  // be stored
  store.dispatch('growl/success', {
    ...growl,
    timeout: UNDO_TIMEOUT,
    action:  {
      label: t('tableViews.tab.undo'),
      run:   async() => {
        // Back under its id, so it comes back to its place in everyone's order
        if (undoShared) {
          if (!await undoShared()) {
            return;
          }
        } else {
          const views = [...savedViews.value];
          const back = Math.min(Math.max(at, 0), views.length);
          // All may have been moved since: it shifts when the view comes back before it, or at its place
          // when it was before it then
          const allNow = allTabIndex.value;
          const allShifts = back < allNow || (back === allNow && beforeAll);

          views.splice(back, 0, saved);
          persistAll(views, wasDefault ? saved.id : ownDefaultViewId.value, allNow + (allShifts ? 1 : 0), orderBefore || undefined);
        }

        if (draft) {
          drafts.value = { ...drafts.value, [draftKey(saved.id)]: draft };
        }

        if (wasSelected) {
          applyView(saved);
        }
      },
    },
  });
};

/** The strip's order as it stands, so a view moving between the user's and the shared keeps its place */
const currentOrder = () => baseTabs.value.map(tabKey);

const publish = async(view: TableViewSaved) => {
  const order = currentOrder();

  if (await share(view)) {
    persistOrder(order, savedViews.value.filter((v) => v.id !== view.id));
  }
};

/** Shared as saved; edits held on the tab stay edits. Asked first, as everyone will see it */
const shareView = (tab: Tab) => {
  const view = tab.view;

  if (!view || tab.shared) {
    return;
  }

  modal.value = { kind: 'publish', view };
};

/** Back to the user's alone: a view of their own first, so it is never lost if the cluster says no */
const unshareView = async(tab: Tab) => {
  const view = tab.view;

  if (!view || !tab.shared) {
    return;
  }

  const order = currentOrder();
  const before = savedViews.value;
  const copy = Object.fromEntries(Object.entries(view).filter(([, v]) => v !== null && v !== '')) as TableViewSaved;

  persistOrder(order, before.filter((v) => v.id !== view.id).concat([copy]));

  if (!(await removeShared(view.id)).ok) {
    persistOrder(order, before);
  }
};

const doExport = (format: string) => {
  const exported = modal.value?.kind === 'export' ? modal.value : null;
  // Kept for the notification, which outlives the modal
  const name = exported?.name || t('tableViews.tabs.all');

  emit('export', {
    format, name, view: exported?.view
  });
  closeModal();
};

/**
 * A view imported into a tab replaces that tab's settings, as unsaved changes to save or discard, and
 * the tab is brought in front to show them. It keeps its own name: the shared view's isn't taken
 */
const importIntoTab = (tabId: string, imported: SharedTableView) => {
  const tab = (tabs.value || []).find((candidate) => candidate.id === tabId);

  if (!tab?.view) {
    return;
  }

  const view = viewStateOf(imported);

  if (tab.id === selectedViewId.value) {
    // Pinned, or a view matched by its config would give way to another tab
    pickedViewId.value = tab.id;
    emit('update:view', view);
  } else {
    // Held for the tab, then shown: the tab left keeps any edits of its own
    drafts.value = { ...drafts.value, [draftKey(tab.id)]: view };
    applyView(tab.view);
  }

  focusTab(tab.id);
};

/**
 * Once the modal has gone and handed the focus back to the menu it was opened from, or that would
 * take the focus off the tab just changed
 */
const importFromModal = (imported: SharedTableView) => {
  if (modal.value?.kind !== 'import') {
    return;
  }

  const { tabId } = modal.value;

  closeModal();

  nextTick(() => setTimeout(() => {
    if (tabId) {
      importIntoTab(tabId, imported);

      return;
    }

    // From the table's own tab: into a view added for it, as Add View adds one, once its tab is there
    const added = addView();

    nextTick(() => importIntoTab(added.id, imported));
  }));
};

const SHORTCUT_ACTIONS: Record<TableViewShortcutAction, () => void> = {
  saveChanges, openSaveAsNew, duplicateCurrent
};

watch(tabQueries, (queries) => emit('tab-queries', queries), { immediate: true });

/**
 * Shared views can come after the list has opened the first time, so a default among them is
 * opened when they arrive - unless the user has picked a tab or changed the table since. Once in,
 * a later change to them never moves the table: the list opened on its default already
 */
if (!sharedLoaded.value) {
  const stopOpeningSharedDefault = watch(sharedLoaded, (isLoaded) => {
    if (!isLoaded) {
      return;
    }

    stopOpeningSharedDefault();

    const id = defaultViewId.value;
    const view = id ? allViews.value.find((candidate) => candidate.id === id) : undefined;

    if (view && pickedViewId.value === undefined && !isViewModified(props.view)) {
      applyView(view);
    }
  });
}

/** The keys are bound in ResourceTable's shortkeys template; the list the focus is in answers them */
let unregisterShortcuts: (() => void) | null = null;

onMounted(() => {
  unregisterShortcuts = registerTableViewShortcuts({
    owns: ownsTarget,
    run:  (action) => SHORTCUT_ACTIONS[action](),
  });
});

onBeforeUnmount(() => {
  unregisterShortcuts?.();
  clearTimeout(flashTimer);
  cancelAnimationFrame(flashFrame);
});

return (_ctx: any,_cache: any) => {
  const _directive_clean_tooltip = _resolveDirective("clean-tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      ref_key: "root",
      ref: root,
      class: "table-views",
      "data-testid": "table-views-tabs"
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          role: "tablist",
          "aria-label": _unref(t)('tableViews.tabs.label'),
          "aria-owns": tabs.value.map(tabElementId).join(' ')
        }, null, 8, _hoisted_2),
        _cache[42] || (_cache[42] = _createTextVNode()),
        _createElementVNode("div", {
          ref_key: "tabStrip",
          ref: tabStrip,
          class: "view-tabs",
          onScroll: closeTabMenus
        }, [
          _createVNode(_TransitionGroup, {
            tag: "div",
            name: "view-tab",
            class: _normalizeClass(["view-tabs-list", { 'is-reordering': _unref(heldTabKey) !== null }])
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (tab) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: tab.id || 'all',
                  ref_for: true,
                  ref: (el) => keepRef(_unref(tabWraps), tab.id || 'all', el),
                  class: _normalizeClass(["view-tab-wrap", {
              active: selectedViewId.value === tab.id,
              held: _unref(heldTabKey) === (tab.id || 'all'),
              flash: flashTabId.value === (tab.id || 'all'),
            }]),
                  onMousedown: ($event: any) => (startTabDrag(tab, $event))
                }, [
                  (!tab.isDefaultTab && renamingId.value === tab.id)
                    ? _withDirectives((_openBlock(), _createElementBlock("input", {
                        key: 0,
                        ref_for: true,
                        ref: (el) => keepRef(_unref(renameInputs), tab.id || '', el),
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((renameDraft).value = $event)),
                        type: "text",
                        class: "view-tab rename-input",
                        size: Math.max(renameDraft.value.length, 4),
                        "aria-label": _unref(t)('tableViews.tab.rename'),
                        "data-testid": `table-views-rename-input-${ tab.id }`,
                        onKeydown: [
                          _withKeys(_withModifiers(commitRename, ["prevent"]), ["enter"]),
                          _withKeys(_withModifiers(cancelRename, ["prevent"]), ["esc"])
                        ],
                        onBlur: commitRename,
                        onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                      }, null, 40, _hoisted_4)), [
                        [_vModelText, renameDraft.value]
                      ])
                    : (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        id: tabElementId(tab),
                        ref_for: true,
                        ref: (el) => keepRef(_unref(tabButtons), tab.id || 'all', el),
                        type: "button",
                        role: "tab",
                        class: "view-tab",
                        "aria-selected": selectedViewId.value === tab.id,
                        tabindex: tab.id === focusableTabId.value ? 0 : -1,
                        "data-testid": tab.isDefaultTab ? 'table-views-tab-all' : `table-views-tab-${ tab.id }`,
                        onClick: ($event: any) => (applyView(tab.view || null)),
                        onKeydown: [
                          _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (stepTab(-1)), ["prevent"]), ["left"])),
                          _cache[3] || (_cache[3] = _withKeys(_withModifiers(($event: any) => (stepTab(1)), ["prevent"]), ["right"])),
                          _cache[4] || (_cache[4] = _withKeys(_withModifiers(($event: any) => (edgeTab('first')), ["prevent"]), ["home"])),
                          _cache[5] || (_cache[5] = _withKeys(_withModifiers(($event: any) => (edgeTab('last')), ["prevent"]), ["end"])),
                          _withKeys(_withModifiers(($event: any) => (openTabMenu(tab)), ["prevent"]), ["down"])
                        ]
                      }, [
                        (tab.shared)
                          ? _withDirectives((_openBlock(), _createElementBlock("i", {
                              key: 0,
                              class: "icon icon-groups shared-mark",
                              role: "img",
                              "aria-label": _unref(t)('tableViews.tab.shared'),
                              "data-testid": `table-views-shared-${ tab.id }`
                            }, null, 8, _hoisted_6)), [
                              [_directive_clean_tooltip, _unref(t)('tableViews.tab.shared')]
                            ])
                          : _createCommentVNode("", true),
                        _createTextVNode(" " + _toDisplayString(tabLabel(tab)) + " ", 1),
                        (isTabDirty(tab))
                          ? _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_7, null, 512)), [
                              [_directive_clean_tooltip, _unref(t)('tableViews.view.unsavedShort')]
                            ])
                          : _createCommentVNode("", true)
                      ], 40, _hoisted_5)),
                  _cache[39] || (_cache[39] = _createTextVNode()),
                  _createVNode(_unref(RcDropdown), {
                    open: openTabMenuId.value === (tab.id || 'all'),
                    placement: 'bottom-start',
                    distance: 9,
                    "reference-node": () => tabWrap(tab),
                    "onUpdate:open": ($event: any) => (onTabMenuToggle(tab, $event))
                  }, {
                    dropdownCollection: _withCtx(() => [
                      _createElementVNode("div", {
                        class: _normalizeClass(['menu-panel', { 'has-notice': isTabDirty(tab) }])
                      }, [
                        (isTabDirty(tab))
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _createElementVNode("div", _hoisted_8, [
                                _cache[7] || (_cache[7] = _createElementVNode("span", { class: "unsaved-dot" }, null, -1)),
                                _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.view.unsaved')), 1)
                              ]),
                              _cache[11] || (_cache[11] = _createTextVNode()),
                              (!tab.isDefaultTab && (!tab.shared || _unref(canEditShared)(tab.id)))
                                ? (_openBlock(), _createBlock(_unref(RcDropdownItem), {
                                    key: 0,
                                    "data-testid": "table-views-save-changes",
                                    onClick: ($event: any) => (saveChanges(tab))
                                  }, {
                                    before: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                      _createElementVNode("i", { class: "icon icon-download" }, null, -1)
                                    ]))]),
                                    after: _withCtx(() => [
                                      _createElementVNode("span", _hoisted_9, _toDisplayString(shortcuts.value.save), 1)
                                    ]),
                                    default: _withCtx(() => [
                                      _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.view.saveChanges')) + " ", 1)
                                    ]),
                                    _: 1
                                  }, 8, ["onClick"]))
                                : _createCommentVNode("", true),
                              _cache[12] || (_cache[12] = _createTextVNode()),
                              _createVNode(_unref(RcDropdownItem), {
                                "data-testid": "table-views-save-as-new",
                                onClick: ($event: any) => (openSaveAsNew(tab))
                              }, {
                                before: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                  _createElementVNode("i", { class: "menu-gutter" }, null, -1)
                                ]))]),
                                after: _withCtx(() => [
                                  _createElementVNode("span", _hoisted_10, _toDisplayString(shortcuts.value.saveAsNew), 1)
                                ]),
                                default: _withCtx(() => [
                                  _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.view.saveAsNew')) + " ", 1)
                                ]),
                                _: 1
                              }, 8, ["onClick"]),
                              _cache[13] || (_cache[13] = _createTextVNode()),
                              _createVNode(_unref(RcDropdownItem), {
                                "data-testid": "table-views-discard",
                                onClick: ($event: any) => (discardChanges(tab))
                              }, {
                                before: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                  _createElementVNode("i", { class: "menu-gutter" }, null, -1)
                                ]))]),
                                default: _withCtx(() => [
                                  _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.view.discard')), 1)
                                ]),
                                _: 1
                              }, 8, ["onClick"]),
                              _cache[14] || (_cache[14] = _createTextVNode()),
                              _createVNode(_unref(RcDropdownSeparator))
                            ], 64))
                          : _createCommentVNode("", true),
                        _cache[27] || (_cache[27] = _createTextVNode()),
                        (!tab.isDefaultTab && (!tab.shared || _unref(canEditShared)(tab.id)))
                          ? (_openBlock(), _createBlock(_unref(RcDropdownItem), {
                              key: 1,
                              "data-testid": `table-views-rename-${ tab.id }`,
                              onClick: ($event: any) => (openRename(tab.view))
                            }, {
                              before: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                _createElementVNode("i", { class: "icon icon-edit" }, null, -1)
                              ]))]),
                              default: _withCtx(() => [
                                _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.tab.rename')), 1)
                              ]),
                              _: 1
                            }, 8, ["data-testid", "onClick"]))
                          : _createCommentVNode("", true),
                        _cache[28] || (_cache[28] = _createTextVNode()),
                        _createVNode(_unref(RcDropdownItem), {
                          "data-testid": tab.isDefaultTab ? 'table-views-duplicate-all' : `table-views-duplicate-${ tab.id }`,
                          onClick: ($event: any) => (duplicateTab(tab))
                        }, {
                          before: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                            _createElementVNode("i", { class: "icon icon-copy" }, null, -1)
                          ]))]),
                          after: _withCtx(() => [
                            _createElementVNode("span", _hoisted_11, _toDisplayString(shortcuts.value.duplicate), 1)
                          ]),
                          default: _withCtx(() => [
                            _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.tab.duplicate')) + " ", 1)
                          ]),
                          _: 1
                        }, 8, ["data-testid", "onClick"]),
                        _cache[29] || (_cache[29] = _createTextVNode()),
                        (!tab.isDefaultTab)
                          ? (_openBlock(), _createBlock(_unref(RcDropdownItem), {
                              key: 2,
                              disabled: !_unref(isViewModified)(tabState(tab)),
                              "data-testid": `table-views-reset-${ tab.id }`,
                              onClick: ($event: any) => (resetTab(tab))
                            }, {
                              before: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                _createElementVNode("i", { class: "menu-gutter" }, null, -1)
                              ]))]),
                              default: _withCtx(() => [
                                _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.view.reset')), 1)
                              ]),
                              _: 1
                            }, 8, ["disabled", "data-testid", "onClick"]))
                          : _createCommentVNode("", true),
                        _cache[30] || (_cache[30] = _createTextVNode()),
                        _createVNode(_unref(RcDropdownItem), {
                          "data-testid": tab.isDefaultTab ? 'table-views-export-all' : `table-views-export-${ tab.id }`,
                          onClick: ($event: any) => (openExport(tab))
                        }, {
                          before: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                            _createElementVNode("i", { class: "menu-gutter" }, null, -1)
                          ]))]),
                          default: _withCtx(() => [
                            _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.export.label')), 1)
                          ]),
                          _: 1
                        }, 8, ["data-testid", "onClick"]),
                        _cache[31] || (_cache[31] = _createTextVNode()),
                        _createVNode(_unref(RcDropdownItem), {
                          class: _normalizeClass({ selected: isDefaultTab(tab) }),
                          "data-testid": tab.isDefaultTab ? 'table-views-set-default-all' : `table-views-set-default-${ tab.id }`,
                          onClick: ($event: any) => (setDefaultView(tab))
                        }, _createSlots({
                          before: _withCtx(() => [
                            _cache[19] || (_cache[19] = _createElementVNode("i", { class: "menu-gutter" }, null, -1))
                          ]),
                          default: _withCtx(() => [
                            _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.tab.setDefault')) + " ", 1)
                          ]),
                          _: 2
                        }, [
                          (isDefaultTab(tab))
                            ? {
                                name: "after",
                                fn: _withCtx(() => [
                                  _cache[20] || (_cache[20] = _createElementVNode("i", { class: "icon icon-checkmark" }, null, -1))
                                ]),
                                key: "0"
                              }
                            : undefined
                        ]), 1032, ["class", "data-testid", "onClick"]),
                        _cache[32] || (_cache[32] = _createTextVNode()),
                        (!tab.isDefaultTab && !tab.shared && _unref(canShare))
                          ? (_openBlock(), _createBlock(_unref(RcDropdownItem), {
                              key: 3,
                              "data-testid": `table-views-publish-${ tab.id }`,
                              onClick: ($event: any) => (shareView(tab))
                            }, {
                              before: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                _createElementVNode("i", { class: "icon icon-groups" }, null, -1)
                              ]))]),
                              default: _withCtx(() => [
                                _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.tab.share')), 1)
                              ]),
                              _: 1
                            }, 8, ["data-testid", "onClick"]))
                          : _createCommentVNode("", true),
                        _cache[33] || (_cache[33] = _createTextVNode()),
                        (tab.shared && _unref(canRemoveShared)(tab.id))
                          ? (_openBlock(), _createBlock(_unref(RcDropdownItem), {
                              key: 4,
                              "data-testid": `table-views-unpublish-${ tab.id }`,
                              onClick: ($event: any) => (unshareView(tab))
                            }, {
                              before: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                                _createElementVNode("i", { class: "menu-gutter" }, null, -1)
                              ]))]),
                              default: _withCtx(() => [
                                _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.tab.unshare')), 1)
                              ]),
                              _: 1
                            }, 8, ["data-testid", "onClick"]))
                          : _createCommentVNode("", true),
                        _cache[34] || (_cache[34] = _createTextVNode()),
                        _createVNode(_unref(RcDropdownSeparator)),
                        _cache[35] || (_cache[35] = _createTextVNode()),
                        _createVNode(_unref(RcDropdownItem), {
                          "data-testid": tab.isDefaultTab ? 'table-views-share-all' : `table-views-share-${ tab.id }`,
                          onClick: ($event: any) => (openShare(tab))
                        }, {
                          before: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                            _createElementVNode("i", { class: "menu-gutter" }, null, -1)
                          ]))]),
                          default: _withCtx(() => [
                            _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.tab.shareView')), 1)
                          ]),
                          _: 1
                        }, 8, ["data-testid", "onClick"]),
                        _cache[36] || (_cache[36] = _createTextVNode()),
                        _createVNode(_unref(RcDropdownItem), {
                          "data-testid": tab.isDefaultTab ? 'table-views-import-all' : `table-views-import-${ tab.id }`,
                          onClick: ($event: any) => (openImport(tab))
                        }, {
                          before: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                            _createElementVNode("i", { class: "menu-gutter" }, null, -1)
                          ]))]),
                          default: _withCtx(() => [
                            _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.tab.importView')), 1)
                          ]),
                          _: 1
                        }, 8, ["data-testid", "onClick"]),
                        _cache[37] || (_cache[37] = _createTextVNode()),
                        (!tab.isDefaultTab && (!tab.shared || _unref(canRemoveShared)(tab.id)))
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                              _createVNode(_unref(RcDropdownSeparator)),
                              _cache[26] || (_cache[26] = _createTextVNode()),
                              _createVNode(_unref(RcDropdownItem), {
                                "data-testid": `table-views-delete-${ tab.id }`,
                                onClick: ($event: any) => (deleteView(tab.view))
                              }, {
                                before: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                                  _createElementVNode("i", { class: "icon icon-trash" }, null, -1)
                                ]))]),
                                default: _withCtx(() => [
                                  _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.tab.delete')), 1)
                                ]),
                                _: 1
                              }, 8, ["data-testid", "onClick"])
                            ], 64))
                          : _createCommentVNode("", true)
                      ], 2)
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_unref(RcDropdownTrigger), {
                        ref_for: true,
                        ref: (el) => keepRef(_unref(tabCarets), tab.id || 'all', el),
                        variant: "link",
                        class: "view-tab-caret",
                        tabindex: "-1",
                        "aria-label": _unref(t)('tableViews.tab.menu'),
                        "data-testid": tab.isDefaultTab ? 'table-views-tab-menu-all' : `table-views-tab-menu-${ tab.id }`
                      }, {
                        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                          _createElementVNode("i", { class: "icon icon-chevron-down" }, null, -1)
                        ]))]),
                        _: 1
                      }, 8, ["aria-label", "data-testid"]),
                      _cache[38] || (_cache[38] = _createTextVNode())
                    ]),
                    _: 2
                  }, 1032, ["open", "reference-node", "onUpdate:open"])
                ], 42, _hoisted_3))
              }), 128))
            ]),
            _: 1
          }, 8, ["class"]),
          _cache[41] || (_cache[41] = _createTextVNode()),
          _createElementVNode("button", {
            type: "button",
            class: "view-tab new-view-tab",
            "data-testid": "table-views-new-tab",
            onClick: addView
          }, [
            _cache[40] || (_cache[40] = _createElementVNode("i", { class: "icon icon-plus" }, null, -1)),
            _createTextVNode(" " + _toDisplayString(_unref(t)('tableViews.tabs.newView')), 1)
          ])
        ], 544)
      ])
    ], 512),
    _cache[43] || (_cache[43] = _createTextVNode()),
    (modal.value && modal.value.kind === 'export')
      ? (_openBlock(), _createBlock(TableViewExportModal, {
          key: 0,
          modal: modalFrame,
          count: modal.value.count,
          "view-name": modal.value.name,
          onExport: doExport
        }, null, 8, ["count", "view-name"]))
      : (modal.value && modal.value.kind === 'publish')
        ? (_openBlock(), _createBlock(TableViewPublishModal, {
            key: 1,
            modal: modalFrame,
            "has-filter": !!modal.value.view.query,
            onPublish: publishFromModal
          }, null, 8, ["has-filter"]))
        : (modal.value)
          ? (_openBlock(), _createBlock(TableViewShareModal, {
              key: 2,
              modal: modalFrame,
              mode: modal.value.kind,
              value: modal.value.kind === 'share' ? modal.value.text : '',
              "has-filter": modal.value.kind === 'share' && modal.value.hasFilter,
              "table-key": tableKey.value,
              "into-new-view": modal.value.kind === 'import' && !modal.value.tabId,
              onImport: importFromModal
            }, null, 8, ["mode", "value", "has-filter", "table-key", "into-new-view"]))
          : _createCommentVNode("", true)
  ], 64))
}
}

})